import type { RenderMode } from '../templates/types';

/** The parts of the legacy Wix SDK (wix.min.js, loaded in widget.html) the widget uses. */
type WixSdk = {
    Utils: { getCompId(): string };
    setHeight(height: number, options?: { overflow: boolean }): void;
};

const sdk = () => (window as unknown as { Wix?: WixSdk }).Wix;

function attempt<T>(read: () => T): T | undefined {
    try {
        return read();
    } catch {
        return undefined;
    }
}

export type WidgetEnv = {
    /** The signed instance Wix appends to the iframe URL. */
    instance: string | null;
    compId: string | null;
    /** Set when the owner duplicated this widget from another one. */
    originCompId: string | null;
    mode: RenderMode;
};

export function readEnv(search = window.location.search): WidgetEnv {
    const query = new URLSearchParams(search);
    const viewMode = query.get('viewMode');
    return {
        instance: query.get('instance'),
        compId: attempt(() => sdk()?.Utils.getCompId()) || query.get('compId'),
        originCompId: query.get('originCompId'),
        mode: viewMode === 'editor' ? 'editor' : viewMode === 'preview' ? 'preview' : 'site',
    };
}

/** Tells Wix how tall the widget's iframe should be. A no-op outside Wix. */
export function setHeight(px: number) {
    attempt(() => sdk()?.setHeight(Math.max(1, Math.ceil(px))));
}

/** Reads the instance payload without verifying it, to decide which setup calls to make. */
export function peekInstance(instance: string): { originInstanceId?: string } {
    const payload = instance.split('.')[1];
    if (!payload) return {};
    return attempt(() => JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))) ?? {};
}
